import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserNamePipe } from '@verify/shared-components/helpers';
import {
  AssetFileSize,
  Notification,
  TenantFeature,
  TenantInfo,
  User,
  UserRule,
} from '@verify/shared-components/models';
import {
  AssetUrlPipe,
  AuthService,
  DialogService,
  HasFeaturePipe,
  HasRulePipe,
  MediaMatchPipe,
  StorageService,
} from '@verify/shared-components/services';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgScrollReachDrop } from 'ngx-scrollbar/reached-event';
import { filter, Observable, Subject, take, takeUntil, tap } from 'rxjs';
import { setPrimaryAndAccentColor } from '../../helpers';
import { NotificationService } from '../../services';
import { AssetMatchService } from '../../services/asset/asset-match.service';
import { NotificationComponent } from './notification/notification.component';
import { SearchComponent } from './search/search.component';

@Component({
  selector: 'app-layout',
  imports: [
    CommonModule,
    RouterModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    MatSidenavModule,
    UserNamePipe,
    MatBadgeModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayModule,
    NotificationComponent,
    TranslateModule,
    NgScrollbarModule,
    NgScrollReachDrop,
    AssetUrlPipe,
    MediaMatchPipe,
    HasRulePipe,
    HasFeaturePipe,
    MatProgressSpinnerModule,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LayoutComponent implements OnInit, OnDestroy {
  private authService = inject(AuthService);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private dialogService = inject(DialogService);
  private storageService = inject(StorageService);
  private assetMatchService = inject(AssetMatchService);
  private cdr = inject(ChangeDetectorRef);

  private destroy$ = new Subject<void>();

  AssetFileSize = AssetFileSize;
  UserRule = UserRule;
  TenantFeature = TenantFeature;
  notificationCount$: Observable<number>;
  notifications$: Observable<Notification[]>;
  notifications: Notification[];
  notificationsOpen = false;
  searchToggle = false;
  searchOpen = false;

  searchValue = '';
  notificationMax = 20;
  menuHover = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menuTimer: any;
  uploads$ = this.storageService.uploads$;
  exportingMatches = this.assetMatchService.exporting;

  ngOnInit(): void {
    this.authService.tenant$
      .pipe(takeUntil(this.destroy$))
      .subscribe((tenant) => {
        setPrimaryAndAccentColor(tenant?.brandColor);
      });
    this.authService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter((user) => !!user),
      )
      .subscribe((currentUser) => {
        if (
          currentUser?.language &&
          this.translateService.currentLang !== currentUser.language
        ) {
          this.translateService.use(currentUser.language.toLocaleLowerCase());
        }
        this.notificationCount$ =
          this.notificationService.getNotificationCounter();
        this.notifications$ = this.notificationService.getNotifications();
        this.onLoadNotifications();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get tenantInfo$(): Observable<TenantInfo> {
    return this.authService.tenantInfo$;
  }

  get user(): User {
    return this.authService.currentUser;
  }

  get initials(): string {
    if (this.user) {
      const { firstName, lastName, email } = this.user;
      if (firstName || lastName) {
        return `${firstName?.substring(0, 1)}${lastName?.substring(0, 1)}`;
      } else {
        return email.substring(0, 1);
      }
    } else {
      return '';
    }
  }

  onMenuEnter(): void {
    this.menuTimer = setTimeout(() => {
      this.menuHover = true;
      this.cdr.markForCheck();
    }, 500);
  }

  onMenuLeave(): void {
    clearTimeout(this.menuTimer);
    this.menuHover = false;
  }

  onSearch(searchValue: string): void {
    this.searchValue = searchValue;
    this.searchOpen = true;
  }

  onCloseSearch(): void {
    this.searchOpen = false;
  }

  onToggleNotifications(): void {
    this.notificationsOpen = !this.notificationsOpen;
    if (this.notificationsOpen) {
      this.notifications$.pipe(take(1)).subscribe((notifications) => {
        const unreadNotifications = notifications.filter(
          (notification) => !notification.read,
        );
        if (unreadNotifications.length > 0) {
          this.notificationService.markNotificationsAsRead(
            unreadNotifications.map((notification) => notification.id),
          );
        }
      });
    }
  }

  onToggleSearch(): void {
    //this.searchToggle = !this.searchToggle;
    this.dialogService.openDialog<void>(SearchComponent, {
      width: this.dialogService.widths.mediumLarge,
      data: {},
      position: { top: '150px' },
      autoFocus: 'input',
    });
  }

  onLoadNotifications(): void {
    this.notifications$ = this.notificationService
      .getNotifications(this.notificationMax)
      .pipe(
        tap((notifications) => {
          this.notifications = notifications;
        }),
      );
    this.notificationMax += 20;
  }
}
